import { useMemo } from 'react';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ApprovalOperation, ApprovalTypes } from 'ui-product-approvals-common';
import { APPROVAL_MODAL_CLOSED_WITHOUT_SUBMISSION, APPROVAL_MODAL_OPENED } from '../../constants/trackingConstants';
import { ErrorSubCategory } from '../../constants/errorSubCategory';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { useApprovers } from '../../hooks/useApprovers';
import { hasUnavailableTemplateApprover } from '../../utils/approvalTemplateUtils';
import { trackInteraction } from '../../utils/usageTracker';
import { ApprovalErrorAlert } from '../ApprovalErrorAlert';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { ApprovalModalForm } from './ApprovalModalForm';
import { ApprovalModalHeader } from './ApprovalModalHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getModalUse = (approvalOperation, approvalType) => {
  if (approvalOperation === ApprovalOperation.REJECT && (approvalType === ApprovalTypes.DEAL || approvalType === ApprovalTypes.CONNECTED_APP)) {
    return 'danger';
  }
  return undefined;
};
const ApprovalModal = ({
  onClose,
  alertContent,
  showFeedbackSurvey,
  onRequestSuccess
}) => {
  const approval = useApprovalContext();
  const {
    approvalAction,
    approvalType,
    currentOperation,
    isLoading
  } = approval.meta;
  const {
    approvers,
    isError: isFetchApproversError,
    isLoading: isLoadingApprovers
  } = useApprovers(approvalType, approvalAction);
  const isRequestOperation = currentOperation === ApprovalOperation.REQUEST;
  const template = isRequestOperation ? approval.config.approvalTemplate : undefined;
  const hasUnavailableApprover = useMemo(() => hasUnavailableTemplateApprover(template, approvers), [approvers, template]);
  if (currentOperation === null) {
    return null;
  }
  const trackModalInteraction = (interaction, extra) => trackInteraction(`${interaction}-${currentOperation}`, approvalType, extra);
  const onCancel = () => {
    trackModalInteraction(APPROVAL_MODAL_CLOSED_WITHOUT_SUBMISSION);
    onClose();
  };
  const modalUse = getModalUse(currentOperation, approvalType);
  const isRequestFormLoading = isRequestOperation && (isLoading || isLoadingApprovers);
  return /*#__PURE__*/_jsxs(Modal, {
    role: "dialog",
    "data-test-id": `product-approvals-approval-modal-${currentOperation}`,
    width: 600,
    onOpenStart: () => trackModalInteraction(APPROVAL_MODAL_OPENED),
    use: modalUse,
    children: [!isRequestOperation && isLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    }), /*#__PURE__*/_jsx(ApprovalModalHeader, {
      approvalType: approvalType,
      approvalOperation: currentOperation,
      onCancel: onCancel
    }), isRequestFormLoading ? /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    }) : isRequestOperation && isFetchApproversError ? /*#__PURE__*/_jsx(DialogBody, {
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.title"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModalBody.fetchError.description"
        })
      })
    }) : isRequestOperation && hasUnavailableApprover ? /*#__PURE__*/_jsx(DialogBody, {
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsx(ApprovalErrorAlert, {
        approvalType: approvalType,
        errorSubCategory: ErrorSubCategory.USER_IS_NOT_AN_APPROVER
      })
    }) : /*#__PURE__*/_jsx(ApprovalModalForm, {
      alertContent: alertContent,
      approvers: approvers,
      buttonUse: modalUse,
      currentOperation: currentOperation,
      onCancel: onCancel,
      onClose: onClose,
      onRequestSuccess: onRequestSuccess,
      showFeedbackSurvey: showFeedbackSurvey,
      template: template
    })]
  });
};
const ApprovalModalWithErrorBoundary = withErrorBoundary(ApprovalModal, 'ApprovalModal');
export { ApprovalModalWithErrorBoundary as ApprovalModal };